import { CircleAlert, Info } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SsoAlertProps {
  /** `notice` for the person's own choice (a cancelled sign-in); `problem` otherwise. */
  tone: 'problem' | 'notice'
  /**
   * Move focus to the alert when it appears — used after a failed invite start,
   * so the message is announced and scrolled into view. A message that arrives
   * with the page (a return from `/auth/callback`) does not steal focus;
   * `role="alert"` announces it.
   */
  autoFocus?: boolean
  /**
   * The i18n key of the message shown, exposed as `data-message-key` so tests
   * assert WHICH message rendered without asserting its copy (fix cycle 1b, A4).
   */
  messageKey?: string
  children: ReactNode
}

/**
 * The one SSO message slot at the top of `/login`'s and `/accept-invite`'s card
 * (CR-SSO-INVITE-PX-2026-09-26). Icon plus text, never colour alone; the text
 * wraps (a long invited address breaks rather than overflowing at 390 px).
 */
export function SsoAlert({ tone, autoFocus = false, messageKey, children }: SsoAlertProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus, children])

  const Icon = tone === 'notice' ? Info : CircleAlert
  return (
    <div
      ref={ref}
      role="alert"
      data-testid="sso-error"
      data-tone={tone}
      data-message-key={messageKey}
      tabIndex={-1}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-xs leading-5 focus-visible:outline-none',
        tone === 'notice'
          ? 'border-border bg-muted text-ink-soft'
          : 'border-blocked/25 bg-blocked-bg text-foreground'
      )}
    >
      <Icon
        aria-hidden
        className={cn(
          'mt-0.5 size-4 shrink-0',
          tone === 'notice' ? 'text-muted-foreground' : 'text-blocked'
        )}
      />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}
